import { z } from 'zod';
import { IsoTimestamp, QuestionId, QuizId, SchoolId } from './common.js';
export const AimsisSyncRequest = z.object({
    dryRun: z.boolean().optional(),
    skipExamsOnBlockingProblems: z.boolean().optional(),
});
const AimsisSyncQuestionBase = z.object({
    id: QuestionId,
    sourceId: z.string().min(1),
    sourceCode: z.string(),
    prompt: z.string(),
    defaultMaxScore: z.number().nonnegative(),
});
const AimsisSyncOption = z.object({
    id: z.string().min(1),
    text: z.string(),
});
export const AimsisSyncQuestion = z.discriminatedUnion('type', [
    AimsisSyncQuestionBase.extend({
        type: z.literal('mcq'),
        options: z.array(AimsisSyncOption),
        correctOptionId: z.string().min(1),
    }),
    AimsisSyncQuestionBase.extend({
        type: z.literal('multipleAnswer'),
        options: z.array(AimsisSyncOption),
        correctOptionIds: z.array(z.string().min(1)).min(1),
    }),
    AimsisSyncQuestionBase.extend({
        type: z.literal('essay'),
        options: z.array(AimsisSyncOption),
    }),
    AimsisSyncQuestionBase.extend({
        type: z.literal('trueFalse'),
        correctValue: z.boolean(),
    }),
    AimsisSyncQuestionBase.extend({
        type: z.literal('labelContent'),
        defaultMaxScore: z.literal(0),
    }),
    AimsisSyncQuestionBase.extend({
        type: z.literal('matching'),
        pairs: z.array(z.object({
            leftId: z.string().min(1).max(32),
            leftText: z.string().min(1).max(2000),
            rightId: z.string().min(1).max(32),
            rightText: z.string().min(1).max(2000),
        })).min(2).max(50),
        correctPairs: z.array(z.object({
            leftId: z.string().min(1).max(32),
            rightId: z.string().min(1).max(32),
        })).min(1).max(50),
    }),
]).superRefine((question, ctx) => {
    if (question.type !== 'matching')
        return;
    const leftIds = question.pairs.map((pair) => pair.leftId);
    const rightIds = question.pairs.map((pair) => pair.rightId);
    if (new Set(leftIds).size !== leftIds.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pairs'], message: 'matching left IDs must be unique' });
    }
    if (new Set(rightIds).size !== rightIds.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pairs'], message: 'matching right IDs must be unique' });
    }
    const solutionKeys = question.correctPairs.map((pair) => `${pair.leftId}\u0000${pair.rightId}`);
    if (new Set(solutionKeys).size !== solutionKeys.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['correctPairs'], message: 'matching solutions must be unique' });
    }
    if (question.correctPairs.some((pair) => !leftIds.includes(pair.leftId) || !rightIds.includes(pair.rightId))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['correctPairs'],
            message: 'matching solutions must reference existing left and right IDs',
        });
    }
});
export const AimsisSyncExamQuestionRef = z.object({
    questionId: QuestionId,
    order: z.number().int().nonnegative(),
    maxScore: z.number().nonnegative(),
    durationSec: z.number().int().nonnegative().nullable(),
});
/**
 * One `eo_quiz_competency` binding: which AIMSIS gradebook (`score_component`)
 * column this quiz's delivered results land in, per class. `maxScore` is the
 * KKM the teacher entered at authoring. `sourceClassroomId`/`name`/`type` are
 * null when the linked `score_component` row no longer exists — the delivery
 * ingestion inner-join would fail that class, so the row is still carried.
 */
export const AimsisSyncScoreComponent = z.object({
    sourceClassroomId: z.string().min(1).nullable(),
    sourceComponentId: z.string().min(1),
    name: z.string().nullable(),
    type: z.string().nullable(),
    maxScore: z.number().nonnegative(),
});
export const AimsisSyncExam = z.object({
    id: QuizId,
    sourceId: z.string().min(1),
    sourceCode: z.string(),
    title: z.string(),
    subject: z.string().nullable(),
    status: z.literal('draft'),
    sourcePublished: z.boolean(),
    classroomIds: z.array(z.string()),
    scheduleMode: z.enum(['global', 'class']).default('class'),
    schedules: z.array(z.object({
        version: z.literal(1),
        classroomId: z.string(),
        sourceClassroomId: z.string(),
        sourceScheduleId: z.string(),
        startAt: IsoTimestamp,
        endAt: IsoTimestamp,
        durationSec: z.number().int().positive(),
        perQuestionSec: z.number().int().positive().optional(),
        observerUserIds: z.array(z.string()),
        displayTimeZone: z.string().min(1).max(64).optional(),
        source: z.object({
            system: z.literal('aimsis'),
            examId: z.string(),
        }),
    })),
    durationSec: z.number().int().nonnegative(),
    gracePeriodSec: z.number().int().nonnegative(),
    shuffleQuestions: z.boolean(),
    shuffleOptions: z.boolean(),
    passcodeRotationSec: z.number().int().positive().nullable(),
    // Legacy `reset_password_interval` verbatim (minutes). Drives the per-class
    // exam password rotation — deliberately separate from passcodeRotationSec,
    // which is the proctor TOTP step and keeps its own clamped mapping.
    examPasswordRotationMin: z.number().int().min(1).max(1440).nullable(),
    createdBy: z.string(),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    questionRefs: z.array(AimsisSyncExamQuestionRef),
    /**
     * The AIMSIS gradebook binding (`eo_quiz_competency`), one entry per class.
     * Read-only mirror — the binding is authored in AIMSIS; CBT carries it so
     * the quiz knows which gradebook column its delivered results land in.
     */
    aimsisScoreComponents: z.array(AimsisSyncScoreComponent),
});
export const AimsisSyncPayload = z.object({
    schoolId: SchoolId,
    questionBank: z.array(AimsisSyncQuestion),
    exams: z.array(AimsisSyncExam),
    warnings: z.array(z.string()),
});
//# sourceMappingURL=aimsis-sync.js.map